/**
 * ChangeOrgMemberRoleForm — FinalForm modal body for "change a team
 * member's role". Mirrors the InviteOrgMemberForm / ReviewForm pattern:
 * inputs from src/components, validation via util/validators, Cancel +
 * Save button row at the bottom.
 *
 * The list of role options is filtered by the caller (the parent page
 * knows the actor's role + target's current role + canChangeRole rules)
 * so this form doesn't need to know the permission matrix itself —
 * easier to keep policy in one place.
 *
 * Spec: UPDATE.md §3.6.8 — server is the authoritative gate via
 * requirePermission('CHANGE_ROLES') + canChangeRole.
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';

import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import { ROLE_LABELS } from '../../util/orgRoles';
import { Form, FieldSelect, SecondaryButton, PrimaryButton } from '../../components';

import css from './ChangeOrgMemberRoleForm.module.css';

const ChangeOrgMemberRoleFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        className,
        rootClassName,
        handleSubmit,
        intl,
        invalid,
        values,
        availableRoles,
        currentRole,
        changeRoleInProgress,
        changeRoleError,
        onCancel,
      } = fieldRenderProps;

      const newRoleRequired = validators.required(
        intl.formatMessage({ id: 'ChangeOrgMemberRoleForm.newRoleRequired' })
      );

      // Submit only enabled when something actually changes.
      const unchanged = !values?.newRole || values.newRole === currentRole;
      const submitDisabled = invalid || unchanged || changeRoleInProgress;

      const classes = classNames(rootClassName || css.root, className);

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <FieldSelect
            id="ChangeOrgMemberRoleForm.newRole"
            name="newRole"
            label={intl.formatMessage({ id: 'ChangeOrgMemberRoleForm.newRoleLabel' })}
            validate={newRoleRequired}
          >
            {availableRoles.map(r => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </FieldSelect>

          {changeRoleError ? (
            <p className={css.error}>
              <FormattedMessage id="ChangeOrgMemberRoleForm.submitFailed" />
            </p>
          ) : null}

          <div className={css.actions}>
            <SecondaryButton type="button" onClick={onCancel} disabled={changeRoleInProgress}>
              <FormattedMessage id="ChangeOrgMemberRoleForm.cancel" />
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              inProgress={changeRoleInProgress}
              disabled={submitDisabled}
            >
              <FormattedMessage id="ChangeOrgMemberRoleForm.save" />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

ChangeOrgMemberRoleFormComponent.defaultProps = {
  className: null,
  rootClassName: null,
  changeRoleError: null,
  changeRoleInProgress: false,
  availableRoles: [],
};

const { bool, func, string, arrayOf } = PropTypes;

ChangeOrgMemberRoleFormComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  onCancel: func.isRequired,
  availableRoles: arrayOf(string),
  currentRole: string,
  changeRoleInProgress: bool,
  changeRoleError: PropTypes.any,
};

const ChangeOrgMemberRoleForm = compose(injectIntl)(ChangeOrgMemberRoleFormComponent);
ChangeOrgMemberRoleForm.displayName = 'ChangeOrgMemberRoleForm';

export default ChangeOrgMemberRoleForm;
